const L = require('leaflet');
const m = require('mithril');
const entityQuickView = require('./entityQuickView');

// Lightweight overlay manager that renders quick views in a bottom flex tray
// and draws connector lines back to the feature center.
const overlayByMap = new WeakMap();

function ensureOverlay(map) {
  let state = overlayByMap.get(map);
  if (state) return state;

  const mapContainer = map.getContainer();
  if (!mapContainer) return null;

  // Allow multiple popups: stop Leaflet from auto-closing on map clicks
  if (map.options.closePopupOnClick) {
    map.off('click', map.closePopup, map);
    map.options.closePopupOnClick = false;
  }

  const container = document.createElement('div');
  container.className = 'quickview-overlay';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.classList.add('quickview-overlay-lines');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('viewBox', `0 0 ${mapContainer.clientWidth} ${mapContainer.clientHeight}`);

  mapContainer.appendChild(svg);
  mapContainer.appendChild(container);

  // Prevent map zoom/scroll when interacting with the overlay
  const stopMapScroll = (e) => {
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  };
  container.addEventListener('wheel', stopMapScroll, { passive: false });
  container.addEventListener('touchmove', stopMapScroll, { passive: false });
  // Prevent overlay clicks from bubbling to the map (so clicks inside don’t clear all)
  container.addEventListener('click', (e) => {
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  });

  const closeAll = (e) => {
    const target = e?.originalEvent?.target || e?.target;
    if (target && typeof target.closest === 'function') {
      // Skip clicks that originate from overlay content or markers/paths
      if (target.closest('.quickview-overlay') || target.closest('.leaflet-marker-icon') || target.closest('.leaflet-interactive')) {
        return;
      }
    }
    // Close every open overlay item
    const entries = [...stateObj.items.values()];
    entries.forEach(entry => {
      try {
        entry?.popup?.remove();
      } catch (err) {
        if (map && entry?.popup && map.closePopup) map.closePopup(entry.popup);
      }
      removeOverlayItem(map, entry.popup);
    });
  };
  map.on('click', closeAll);
  // Also listen on the DOM container to catch clicks that Leaflet doesn't surface as map events
  const closeAllDom = (evt) => closeAll({ originalEvent: evt });
  mapContainer.addEventListener('click', closeAllDom, true);

  const stateObj = {
    map,
    container,
    svg,
    items: new Map(),
    updateScheduled: false,
    stopMapScroll,
    closeAll,
    closeAllDom
  };

  const scheduleUpdate = () => {
    if (stateObj.updateScheduled) return;
    stateObj.updateScheduled = true;
    requestAnimationFrame(() => {
      stateObj.updateScheduled = false;
      updateLines(stateObj);
    });
  };

  stateObj.scheduleUpdate = scheduleUpdate;

  const resizeObserver = new ResizeObserver(scheduleUpdate);
  resizeObserver.observe(mapContainer);
  stateObj.resizeObserver = resizeObserver;

  const onMove = () => scheduleUpdate();
  map.on('zoom move resize', onMove);
  map.on('unload', () => cleanupOverlay(map));
  stateObj.onMove = onMove;

  overlayByMap.set(map, stateObj);
  return stateObj;
}

function cleanupOverlay(map) {
  const state = overlayByMap.get(map);
  if (!state) return;
  map.off('zoom move resize', state.onMove);
  state.resizeObserver?.disconnect();
  state.items.forEach(entry => {
    try { m.mount(entry.content, null); } catch (e) {}
  });
  if (state.container?.parentNode) state.container.parentNode.removeChild(state.container);
  if (state.svg?.parentNode) state.svg.parentNode.removeChild(state.svg);
  if (state.container) {
    state.container.removeEventListener('wheel', state.stopMapScroll);
    state.container.removeEventListener('touchmove', state.stopMapScroll);
  }
  if (state.closeAll) {
    map.off('click', state.closeAll);
  }
  if (state.closeAllDom) {
    map.getContainer()?.removeEventListener('click', state.closeAllDom, true);
  }
  overlayByMap.delete(map);
}

function addOverlayItem(map, popup, latlng, record) {
  const state = ensureOverlay(map);
  if (!state) return;
  const { container, svg, items, scheduleUpdate } = state;
  const popupId = popup._leaflet_id || L.stamp(popup);

  // Remove any existing entry for this popup id
  removeOverlayItem(map, popup);

  const item = document.createElement('div');
  item.className = 'quickview-overlay-item';

  const content = document.createElement('div');
  content.className = 'quickview-overlay-card';
  item.appendChild(content);

  // Mount the quick view
  m.mount(content, { view: () => m(entityQuickView, { entityRecord: record }) });

  // Close control
  const close = document.createElement('button');
  close.className = 'quickview-overlay-close';
  close.setAttribute('aria-label', 'Close quick view');
  close.innerHTML = '&times;';
  close.onclick = (e) => {
    e.stopPropagation();
    if (map && map.closePopup) map.closePopup(popup);
    removeOverlayItem(map, popup);
  };
  item.appendChild(close);

  container.appendChild(item);

  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line.classList.add('quickview-overlay-line');
  svg.appendChild(line);

  items.set(popupId, { latlng, item, line, content, popup });
  container.classList.add('is-visible');
  scheduleUpdate();
}

function removeOverlayItem(map, popup) {
  const state = overlayByMap.get(map);
  if (!state) return;
  const { container, svg, items, scheduleUpdate } = state;
  const popupId = popup?._leaflet_id || (popup ? L.stamp(popup) : null);
  if (!popupId) return;
  const entry = items.get(popupId);
  if (!entry) return;
  try { m.mount(entry.content, null); } catch (e) {}
  if (entry.item?.parentNode) entry.item.parentNode.removeChild(entry.item);
  if (entry.line?.parentNode) entry.line.parentNode.removeChild(entry.line);
  items.delete(popupId);
  if (!items.size) {
    container.classList.remove('is-visible');
    // When last popup is gone, allow next map click to close nothing (noop)
  }
  scheduleUpdate();
}

function updateLines(state) {
  if (!state || !state.map) return;
  const { map, svg, items } = state;
  const mapContainer = map.getContainer();
  if (!mapContainer) return;

  const bounds = mapContainer.getBoundingClientRect();
  svg.setAttribute('viewBox', `0 0 ${bounds.width} ${bounds.height}`);

  items.forEach(entry => {
    const start = map.latLngToContainerPoint(entry.latlng);
    const itemRect = entry.item.getBoundingClientRect();
    const endX = itemRect.left + (itemRect.width / 2) - bounds.left;
    const endY = itemRect.top - bounds.top;
    entry.line.setAttribute('x1', start.x);
    entry.line.setAttribute('y1', start.y);
    entry.line.setAttribute('x2', endX);
    entry.line.setAttribute('y2', endY);
  });
}

// Factory used by other modules.
function createPopup(layer, locationRecord, map) {
  const layerCenter = layer.getBounds && layer.getBounds().getCenter
    ? layer.getBounds().getCenter()
    : (layer.getLatLng ? layer.getLatLng() : map.getCenter());

  const popup = L.popup({
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    className: 'quickview-hidden-popup'
  }).setLatLng(layerCenter);

  // Store leaflet id early in case add/remove fire before binding
  const popupId = popup._leaflet_id || L.stamp(popup);

  popup.on('add', () => addOverlayItem(map, popup, layerCenter, locationRecord));
  popup.on('remove', () => removeOverlayItem(map, popup));

  return popup;
}

module.exports = createPopup;
